import axios from "axios";
import { BASE_URL } from "../constants";
import { tokenStorage } from "../utils/tokenStorage";
import { fetchRefreshedAuthToken } from "../utils/authRefreshUtils";
import { isBlockedByAdminLoginResponse } from "../utils/authHelpers";
import { forceLogoutOnAdminBlock } from "../utils/forceLogoutOnAdminBlock";
import { isDevelopmentEnv } from "../utils/formatters";
import { isGuestBrowsePath } from "../utils/guestBrowsePaths";
import type { AuthToken } from "../types/auth";

// Extend AxiosRequestConfig to include useAccessToken flag
declare module "axios" {
  export interface AxiosRequestConfig {
    useAccessToken?: boolean;
    skipAuth?: boolean;
    _retry?: boolean;
  }
}

let refreshInFlight: Promise<void> | null = null;

async function syncRefreshedTokensToStore(tokens: AuthToken): Promise<void> {
  const { syncRefreshedAuthTokens } = await import("../utils/syncRefreshedAuthTokens");
  syncRefreshedAuthTokens(tokens);
}

function scheduleRefresh(): Promise<void> {
  if (!refreshInFlight) {
    refreshInFlight = (async () => {
      const rt = tokenStorage.getRefreshToken();
      if (!rt) {
        throw new Error("No refresh token");
      }
      const tokens = await fetchRefreshedAuthToken(rt);
      await syncRefreshedTokensToStore(tokens);
    })().finally(() => {
      refreshInFlight = null;
    });
  }
  return refreshInFlight;
}

export const api = axios.create({
  baseURL: BASE_URL,
  timeout: 10000,
});

// Request interceptor
api.interceptors.request.use(
  (config) => {
    if (!config.skipAuth) {
      const useAccessToken = config.useAccessToken;
      const token = useAccessToken
        ? tokenStorage.getTokens().accessToken
        : tokenStorage.getTokens().userAccessToken;
      if (token && config.headers) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }
    if (config.data instanceof FormData) {
      delete config.headers?.["Content-Type"];
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    const status = error.response?.status;
    const responseData = error.response?.data as
      | Record<string, unknown>
      | undefined;

    if (isBlockedByAdminLoginResponse(responseData)) {
      const hadSession =
        Boolean(tokenStorage.getRefreshToken()) ||
        Boolean(tokenStorage.getUserAccessToken());
      if (hadSession && originalRequest && !originalRequest.skipAuth) {
        await forceLogoutOnAdminBlock();
      }
      return Promise.reject(error);
    }

    if (
      status !== 401 ||
      !originalRequest ||
      originalRequest.skipAuth ||
      originalRequest._retry
    ) {
      return Promise.reject(error);
    }

    const url = typeof originalRequest.url === "string" ? originalRequest.url : "";
    const fullUrl = `${originalRequest.baseURL ?? ""}${url}`;
    if (
      fullUrl.includes("/auth/refresh-token") ||
      fullUrl.includes("/auth/login") ||
      fullUrl.includes("/auth/signup")
    ) {
      return Promise.reject(error);
    }

    const hadAuth = Boolean(originalRequest.headers?.Authorization);
    if (!hadAuth || !tokenStorage.getRefreshToken()) {
      return Promise.reject(error);
    }

    originalRequest._retry = true;

    try {
      await scheduleRefresh();
      const useAccessToken = originalRequest.useAccessToken;
      const token = useAccessToken
        ? tokenStorage.getTokens().accessToken
        : tokenStorage.getTokens().userAccessToken;
      if (token && originalRequest.headers) {
        originalRequest.headers.Authorization = `Bearer ${token}`;
      }
      return api.request(originalRequest);
    } catch {
      tokenStorage.clearTokens();
      if (!isDevelopmentEnv() && typeof window !== "undefined") {
        const path = window.location.pathname;
        if (
          !path.includes("/auth/login") &&
          !path.includes("/auth/signup") &&
          !isGuestBrowsePath(path)
        ) {
          window.location.href = "/";
        }
      }
      return Promise.reject(error);
    }
  }
);
